/**
 * Central Hreflang Configuration
 * Single source of truth for all hreflang logic across the application
 */

import { getLocalizedKnowledgeSlugSync } from '../knowledge/slugMapping';
import blogSlugMappingsEn from '../blog/slugMappings/en.json';
import blogSlugMappingsDe from '../blog/slugMappings/de.json';
import blogSlugMappingsPtBR from '../blog/slugMappings/pt-BR.json';
import blogSlugMappingsMsMY from '../blog/slugMappings/ms-MY.json';
import blogSlugMappingsEs from '../blog/slugMappings/es.json';
import blogSlugMappingsFr from '../blog/slugMappings/fr.json';
import blogSlugMappingsJa from '../blog/slugMappings/ja.json';
import blogSlugMappingsId from '../blog/slugMappings/id.json';
import categorySlugMappingsEn from '../blog/categorySlugs/en.json';
import categorySlugMappingsDe from '../blog/categorySlugs/de.json';
import categorySlugMappingsPtBR from '../blog/categorySlugs/pt-BR.json';
import categorySlugMappingsMsMY from '../blog/categorySlugs/ms-MY.json';
import categorySlugMappingsEs from '../blog/categorySlugs/es.json';
import categorySlugMappingsFr from '../blog/categorySlugs/fr.json';
import categorySlugMappingsJa from '../blog/categorySlugs/ja.json';
import categorySlugMappingsId from '../blog/categorySlugs/id.json';
import knowledgeSlugMappingsEn from '../knowledge/slugMappings/en.json';
import knowledgeSlugMappingsDe from '../knowledge/slugMappings/de.json';
import knowledgeSlugMappingsPtBR from '../knowledge/slugMappings/pt-BR.json';
import knowledgeSlugMappingsMsMY from '../knowledge/slugMappings/ms-MY.json';
import knowledgeSlugMappingsEs from '../knowledge/slugMappings/es.json';
import knowledgeSlugMappingsFr from '../knowledge/slugMappings/fr.json';
import knowledgeSlugMappingsJa from '../knowledge/slugMappings/ja.json';
import knowledgeSlugMappingsId from '../knowledge/slugMappings/id.json';

export const locales: Record<string, string> = {
  en: "",
  de: "/de",
  "pt-BR": "/pt-br",
  "ms-MY": "/my",
  "es": "/es",
  "fr": "/fr",
  "ja": "/ja",
  "id": "/id",
};

/**
 * URL mappings for pages with translated slugs
 * Maps English slug to localized slugs for each language
 */
export const urlMappings: Record<string, Record<string, string>> = {
  "/about": {
    en: "/about",
    de: "/ueber-uns",
    "pt-BR": "/sobre",
    "ms-MY": "/tentang-kami",
    "es": "/acerca-de",
    "fr": "/a-propos",
    "ja": "/about",
    "id": "/tentang-kami",
  },
  "/imprint": {
    en: "/imprint",
    de: "/impressum",
    "pt-BR": "/impressao",
    "ms-MY": "/cetakan",
    "es": "/aviso-legal",
    "fr": "/mentions-legales",
    "ja": "/imprint",
    "id": "/cetakan",
  },
  "/terms": {
    en: "/terms",
    de: "/agb",
    "pt-BR": "/termos",
    "ms-MY": "/terma",
    "es": "/terminos",
    "fr": "/conditions",
    "ja": "/terms",
    "id": "/syarat-ketentuan",
  },
  "/privacy": {
    en: "/privacy",
    de: "/datenschutz",
    "pt-BR": "/privacidade",
    "ms-MY": "/privasi",
    "es": "/privacidad",
    "fr": "/confidentialite",
    "ja": "/privacy",
    "id": "/privasi",
  },
  "/comparison": {
    en: "/comparison",
    de: "/vergleiche",
    "pt-BR": "/comparacao",
    "ms-MY": "/perbandingan",
    "es": "/comparacion",
    "fr": "/comparaison",
    "ja": "/comparison",
    "id": "/perbandingan",
  },
  "/detect": {
    en: "/detect",
    de: "/erkennung",
    "pt-BR": "/detecao",
    "ms-MY": "/pengesanan",
    "es": "/detectar",
    "fr": "/detection",
    "ja": "/detect",
    "id": "/deteksi",
  },
  "/document": {
    en: "/document",
    de: "/dokument",
    "pt-BR": "/documento",
    "ms-MY": "/dokumen",
    "es": "/documento",
    "fr": "/document",
    "ja": "/document",
    "id": "/dokumen",
  },
  "/pricing": {
    en: "/pricing",
    de: "/preise",
    "pt-BR": "/precos",
    "ms-MY": "/harga",
    "es": "/precios",
    "fr": "/tarifs",
    "ja": "/pricing",
    "id": "/harga",
  },
  "/specialdeal": {
    en: "/specialdeal",
    de: "/sonderangebot",
    "pt-BR": "/oferta-especial",
    "ms-MY": "/tawaran-khas",
    "es": "/oferta-especial",
    "fr": "/offre-speciale",
    "ja": "/specialdeal",
    "id": "/penawaran-khusus",
  },
  "/subscription": {
    en: "/subscription",
    de: "/abo",
    "pt-BR": "/assinatura",
    "ms-MY": "/langganan",
    "es": "/suscripcion",
    "fr": "/abonnement",
    "ja": "/subscription",
    "id": "/langganan",
  },
  "/feature-request": {
    en: "/feature-request",
    de: "/feature-anfrage",
    "pt-BR": "/solicitar-recurso",
    "ms-MY": "/permintaan-ciri",
    "es": "/solicitar-funcion",
    "fr": "/demande-fonctionnalite",
    "ja": "/feature-request",
    "id": "/permintaan-fitur",
  },
  // API pages (B2B landing pages)
  "/api": {
    en: "/api",
    de: "/api-schnittstelle",
    "pt-BR": "/api-interface",
    "ms-MY": "/api-antara-muka",
    "es": "/api-interfaz",
    "fr": "/api-interface",
    "ja": "/api",
    "id": "/api-antarmuka",
  },
  "/api/writing-tools": {
    en: "/api/writing-tools",
    de: "/api-schnittstelle/schreibwerkzeuge",
    "pt-BR": "/api-interface/ferramentas-escrita",
    "ms-MY": "/api-antara-muka/alat-penulisan",
    "es": "/api-interfaz/herramientas-escritura",
    "fr": "/api-interface/outils-redaction",
    "ja": "/api/writing-tools",
    "id": "/api-antarmuka/alat-penulisan",
  },
  "/api/chatbot-middleware": {
    en: "/api/chatbot-middleware",
    de: "/api-schnittstelle/chatbot-middleware",
    "pt-BR": "/api-interface/middleware-chatbot",
    "ms-MY": "/api-antara-muka/middleware-chatbot",
    "es": "/api-interfaz/middleware-chatbot",
    "fr": "/api-interface/middleware-chatbot",
    "ja": "/api/chatbot-middleware",
    "id": "/api-antarmuka/middleware-chatbot",
  },
  "/api/content-platforms": {
    en: "/api/content-platforms",
    de: "/api-schnittstelle/content-plattformen",
    "pt-BR": "/api-interface/plataformas-conteudo",
    "ms-MY": "/api-antara-muka/platform-kandungan",
    "es": "/api-interfaz/plataformas-contenido",
    "fr": "/api-interface/plateformes-contenu",
    "ja": "/api/content-platforms",
    "id": "/api-antarmuka/platform-konten",
  },
  // pSEO pages (highest SEO value)
  "/remove-chatgpt-watermarks": {
    en: "/remove-chatgpt-watermarks",
    de: "/chatgpt-wasserzeichen-entfernen",
    "pt-BR": "/remover-marca-dagua-chatgpt",
    "ms-MY": "/buang-tanda-air-chatgpt",
    "es": "/eliminar-marca-de-agua-chatgpt",
    "fr": "/supprimer-filigrane-chatgpt",
    "ja": "/chatgpt-watermark-remover",
    "id": "/hapus-watermark-chatgpt",
  },
  "/remove-gemini-watermarks": {
    en: "/remove-gemini-watermarks",
    de: "/gemini-wasserzeichen-entfernen",
    "pt-BR": "/remover-marca-dagua-gemini",
    "ms-MY": "/buang-tanda-air-gemini",
    "es": "/eliminar-marca-de-agua-gemini",
    "fr": "/supprimer-filigrane-gemini",
    "ja": "/gemini-watermark-remover",
    "id": "/hapus-watermark-gemini",
  },
  "/remove-gemini-image-watermark": {
    en: "/remove-gemini-image-watermark",
    de: "/gemini-bild-wasserzeichen-entfernen",
    "pt-BR": "/remover-marca-dagua-imagem-gemini",
    "ms-MY": "/buang-tanda-air-imej-gemini",
    "es": "/eliminar-marca-agua-imagen-gemini",
    "fr": "/supprimer-filigrane-image-gemini",
    "ja": "/gemini-image-watermark-remover",
    "id": "/hapus-watermark-gambar-gemini",
  },
  "/remove-claude-watermarks": {
    en: "/remove-claude-watermarks",
    de: "/claude-wasserzeichen-entfernen",
    "pt-BR": "/remover-marca-dagua-claude",
    "ms-MY": "/buang-tanda-air-claude",
    "es": "/eliminar-marca-de-agua-claude",
    "fr": "/supprimer-filigrane-claude",
    "ja": "/claude-watermark-remover",
    "id": "/hapus-watermark-claude",
  },
  "/ai-watermark-remover-students": {
    en: "/ai-watermark-remover-students",
    de: "/ki-wasserzeichen-entferner-studenten",
    "pt-BR": "/removedor-ai-estudantes",
    "ms-MY": "/penyingkir-tanda-air-ai-pelajar",
    "es": "/eliminar-marca-de-agua-texto-estudiantes",
    "fr": "/supprimeur-filigrane-ia-etudiants",
    "ja": "/ai-watermark-remover-students",
    "id": "/penghapus-watermark-ai-mahasiswa",
  },
  // Free pSEO pages
  "/free-chatgpt-watermark-remover": {
    en: "/free-chatgpt-watermark-remover",
    de: "/kostenloser-chatgpt-wasserzeichen-entferner",
    "pt-BR": "/removedor-marca-dagua-chatgpt-gratis",
    "ms-MY": "/penyingkir-tanda-air-chatgpt-percuma",
    "es": "/eliminador-marca-agua-chatgpt-gratis",
    "fr": "/supprimeur-filigrane-chatgpt-gratuit",
    "ja": "/free-chatgpt-watermark-remover",
    "id": "/penghapus-watermark-chatgpt-gratis",
  },
  "/free-ai-text-watermark-remover": {
    en: "/free-ai-text-watermark-remover",
    de: "/kostenloser-ki-text-wasserzeichen-entferner",
    "pt-BR": "/removedor-marca-dagua-texto-ia-gratis",
    "ms-MY": "/penyingkir-tanda-air-teks-ai-percuma",
    "es": "/eliminador-marca-agua-texto-ia-gratis",
    "fr": "/supprimeur-filigrane-texte-ia-gratuit",
    "ja": "/free-ai-text-watermark-remover",
    "id": "/penghapus-watermark-teks-ai-gratis",
  },
  "/free-ai-watermark-remover": {
    en: "/free-ai-watermark-remover",
    de: "/kostenloser-ki-wasserzeichen-entferner",
    "pt-BR": "/removedor-marca-dagua-ia-gratis",
    "ms-MY": "/penyingkir-tanda-air-ai-percuma",
    "es": "/eliminador-marca-agua-ia-gratis",
    "fr": "/supprimeur-filigrane-ia-gratuit",
    "ja": "/free-ai-watermark-remover",
    "id": "/penghapus-watermark-ai-gratis",
  },
  "/free-gpt-watermark-remover": {
    en: "/free-gpt-watermark-remover",
    de: "/kostenloser-gpt-wasserzeichen-entferner",
    "pt-BR": "/removedor-marca-dagua-gpt-gratis",
    "ms-MY": "/penyingkir-tanda-air-gpt-percuma",
    "es": "/eliminador-marca-agua-gpt-gratis",
    "fr": "/supprimeur-filigrane-gpt-gratuit",
    "ja": "/free-gpt-watermark-remover",
    "id": "/penghapus-watermark-gpt-gratis",
  },
  // Educational pSEO pages
  "/detect-ai-watermarks": {
    en: "/detect-ai-watermarks",
    de: "/ki-wasserzeichen-erkennen",
    "pt-BR": "/detectar-marcas-dagua-ia",
    "ms-MY": "/kesan-tanda-air-ai",
    "es": "/detectar-marcas-agua-ia",
    "fr": "/detecter-filigranes-ia",
    "ja": "/detect-ai-watermarks",
    "id": "/deteksi-watermark-ai",
  },
  // Knowledge Base routes
  "/knowledge": {
    en: "/knowledge",
    de: "/wissen",
    "pt-BR": "/conhecimento",
    "ms-MY": "/pengetahuan",
    "es": "/conocimiento",
    "fr": "/base-connaissances",
    "ja": "/knowledge",
    "id": "/pengetahuan",
  },
};

/**
 * Get the fully localized URL path for a given English path and locale.
 * Handles both the slug translation AND the locale prefix.
 *
 * @example
 * getLocalizedPath('/remove-claude-watermarks', 'de') → '/de/claude-wasserzeichen-entfernen'
 * getLocalizedPath('/blog', 'pt-BR') → '/pt-br/blog'
 * getLocalizedPath('/about', 'en') → '/about'
 */
export const getLocalizedPath = (englishPath: string, locale: string): string => {
  const withLeadingSlash = englishPath.startsWith('/') ? englishPath : `/${englishPath}`;
  const localePrefix = locales[locale] || '';

  // Check if this path has a translated slug
  const mapping = urlMappings[withLeadingSlash];
  if (mapping && mapping[locale]) {
    const localizedSlug = mapping[locale];
    // For English, no prefix needed
    if (locale === 'en') {
      return localizedSlug;
    }
    // For other locales, combine prefix + localized slug
    return `${localePrefix}${localizedSlug}`;
  }

  // No translation exists - just add locale prefix
  if (locale === 'en') {
    return withLeadingSlash;
  }
  return `${localePrefix}${withLeadingSlash}`;
};

/**
 * Reverse lookup: Given a localized path, find the English canonical path
 * Handles:
 * - Regular pages with translated slugs (e.g., "/de/ueber-uns" → "/about")
 * - Blog posts with localized slugs (e.g., "/de/blog/german-slug" → "/blog/english-slug")
 * - Knowledge articles (e.g., "/de/wissen/slug" → "/knowledge/english-slug")
 */
const getCanonicalPath = (localizedPath: string): string => {
  // First, detect locale and strip the locale prefix
  const locale = localizedPath.match(/^\/(en|de|pt-br|my|es|fr|ja|id)/)?.[1] || 'en';
  const localeMap: Record<string, string> = {
    'en': 'en',
    'de': 'de',
    'pt-br': 'pt-BR',
    'my': 'ms-MY',
    'es': 'es',
    'fr': 'fr',
    'ja': 'ja',
    'id': 'id',
  };
  const detectedLocale = localeMap[locale] || 'en';

  const pathWithoutLocale = localizedPath.replace(/^\/(en|de|pt-br|my|es|fr|ja|id)(?=\/|$)/, "") || "/";

  // Handle blog posts with slug translation
  // Pattern: /blog/localized-slug → /blog/english-slug
  const blogMatch = pathWithoutLocale.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const localizedSlug = blogMatch[1];

    const blogSlugMappings = {
      'de': blogSlugMappingsDe,
      'pt-BR': blogSlugMappingsPtBR,
      'ms-MY': blogSlugMappingsMsMY,
      'es': blogSlugMappingsEs,
      'en': blogSlugMappingsEn,
      'fr': blogSlugMappingsFr,
      'ja': blogSlugMappingsJa,
      'id': blogSlugMappingsId,
    };

    const mapping = blogSlugMappings[detectedLocale as keyof typeof blogSlugMappings];
    if (!mapping) return pathWithoutLocale;

    // Reverse lookup: find English slug from localized slug
    for (const [englishSlug, localSlug] of Object.entries(mapping)) {
      if (localSlug === localizedSlug) {
        return `/blog/${englishSlug}`;
      }
    }

    // If not found in mapping, assume it's already English
    return pathWithoutLocale;
  }

  // Handle blog category pages with localized paths and slugs
  // Pattern: /blog/kategorie/slug OR /blog/categoria/slug OR /blog/kategori/slug OR /blog/categorie/slug → /blog/category/english-id
  const categoryMatch = pathWithoutLocale.match(/^\/blog\/(category|kategorie|categoria|kategori|categorie)\/([^/]+)$/);
  if (categoryMatch) {
    const localizedCategorySlug = categoryMatch[2];

    const categorySlugMappings = {
      'de': categorySlugMappingsDe,
      'pt-BR': categorySlugMappingsPtBR,
      'ms-MY': categorySlugMappingsMsMY,
      'es': categorySlugMappingsEs,
      'en': categorySlugMappingsEn,
      'fr': categorySlugMappingsFr,
      'ja': categorySlugMappingsJa,
      'id': categorySlugMappingsId,
    };

    const mapping = categorySlugMappings[detectedLocale as keyof typeof categorySlugMappings];
    if (!mapping) return pathWithoutLocale;

    // Reverse lookup: find English category ID from localized slug
    for (const [englishId, localSlug] of Object.entries(mapping)) {
      if (localSlug === localizedCategorySlug) {
        return `/blog/category/${englishId}`;
      }
    }

    // If not found in mapping, assume it's already English
    return `/blog/category/${localizedCategorySlug}`;
  }

  // Special handling for knowledge article URLs
  // Pattern: /knowledge/slug or /wissen/slug or /conhecimento/slug or /pengetahuan/slug or /conocimiento/slug or /base-connaissances/slug
  const knowledgeBasePatterns = {
    en: '/knowledge/',
    de: '/wissen/',
    'pt-BR': '/conhecimento/',
    'ms-MY': '/pengetahuan/',
    'es': '/conocimiento/',
    'fr': '/base-connaissances/',
    'ja': '/knowledge/',
    'id': '/pengetahuan/',
  };

  for (const [lang, pattern] of Object.entries(knowledgeBasePatterns)) {
    if (pathWithoutLocale.startsWith(pattern)) {
      // Extract the article slug and convert to English slug
      const localizedSlug = pathWithoutLocale.replace(pattern, '');

      // Reverse lookup using static mappings
      const knowledgeSlugMappings = {
        'de': knowledgeSlugMappingsDe,
        'pt-BR': knowledgeSlugMappingsPtBR,
        'ms-MY': knowledgeSlugMappingsMsMY,
        'es': knowledgeSlugMappingsEs,
        'en': knowledgeSlugMappingsEn,
        'fr': knowledgeSlugMappingsFr,
        'ja': knowledgeSlugMappingsJa,
        'id': knowledgeSlugMappingsId,
      };

      const mapping = knowledgeSlugMappings[detectedLocale as keyof typeof knowledgeSlugMappings];
      if (mapping) {
        // Find English key for this localized value
        for (const [englishSlug, localized] of Object.entries(mapping)) {
          if (localized === localizedSlug) {
            return `/knowledge/${englishSlug}`;
          }
        }
      }

      // If no mapping found, return original slug
      return `/knowledge/${localizedSlug}`;
    }
  }

  // Check if this path exists in our URL mappings as a translated version
  for (const [canonicalPath, translations] of Object.entries(urlMappings)) {
    for (const translation of Object.values(translations)) {
      if (translation === pathWithoutLocale) {
        return canonicalPath;
      }
    }
  }

  // If no mapping found, return the path as-is
  return pathWithoutLocale;
};

/**
 * Input: current route path including prefix (e.g. "/de/ueber-uns", "/br/sobre")
 * Output: normalized English canonical route (e.g. "/about")
 */
export const normalizeRoute = (path: string): string => {
  const canonicalPath = getCanonicalPath(path);
  return canonicalPath === "" ? "/" : canonicalPath;
};

/**
 * Returns all hreflang entries for the normalized route
 * Supports:
 * - Custom URL mappings for translated page slugs
 * - Blog posts with localized slugs
 * - Knowledge articles with localized slugs
 * - Proper return tags for all languages
 */
export const getHreflangLinks = (normalizedPath: string) => {
  const base = "https://gpt-watermark-remover.com";

  return Object.entries(locales).map(([lang, prefix]) => {
    // Special handling for homepage to match canonical rules
    if (normalizedPath === "/") {
      // All homepage variants - consistent trailing slash handling
      // Root / keeps slash, all locale prefixes (/de, /pt-br, /my, /es, /fr, /ja, /id) have no trailing slash
      if (lang === "de") {
        return { lang, url: `${base}/de` };
      } else if (lang === "pt-BR") {
        return { lang, url: `${base}/pt-br` };
      } else if (lang === "ms-MY") {
        return { lang, url: `${base}/my` };
      } else if (lang === "es") {
        return { lang, url: `${base}/es` };
      } else if (lang === "fr") {
        return { lang, url: `${base}/fr` };
      } else if (lang === "ja") {
        return { lang, url: `${base}/ja` };
      } else if (lang === "id") {
        return { lang, url: `${base}/id` };
      } else {
        return { lang, url: `${base}/` };
      }
    }

    // Handle blog posts with slug translation
    // Pattern: /blog/english-slug → /de/blog/german-slug
    const blogMatch = normalizedPath.match(/^\/blog\/([^/]+)$/);
    if (blogMatch) {
      const englishSlug = blogMatch[1];

      const blogSlugMappings = {
        'de': blogSlugMappingsDe,
        'pt-BR': blogSlugMappingsPtBR,
        'ms-MY': blogSlugMappingsMsMY,
        'es': blogSlugMappingsEs,
        'en': blogSlugMappingsEn,
        'fr': blogSlugMappingsFr,
        'ja': blogSlugMappingsJa,
        'id': blogSlugMappingsId,
      };

      const mapping = blogSlugMappings[lang as keyof typeof blogSlugMappings];
      const localizedSlug = mapping?.[englishSlug] || englishSlug;

      return {
        lang,
        url: `${base}${prefix}/blog/${localizedSlug}`,
      };
    }

    // Handle blog category pages with localized paths and slugs
    // Pattern: /blog/category/english-id → /de/blog/kategorie/german-slug
    const categoryMatch = normalizedPath.match(/^\/blog\/category\/([^/]+)$/);
    if (categoryMatch) {
      const englishCategoryId = categoryMatch[1];

      const categorySlugMappings = {
        'de': categorySlugMappingsDe,
        'pt-BR': categorySlugMappingsPtBR,
        'ms-MY': categorySlugMappingsMsMY,
        'es': categorySlugMappingsEs,
        'en': categorySlugMappingsEn,
        'fr': categorySlugMappingsFr,
        'ja': categorySlugMappingsJa,
        'id': categorySlugMappingsId,
      };

      const categoryPathSegments = {
        'en': 'category',
        'de': 'kategorie',
        'pt-BR': 'categoria',
        'ms-MY': 'kategori',
        'es': 'categoria',
        'fr': 'categorie',
        'ja': 'category',
        'id': 'kategori',
      };

      const mapping = categorySlugMappings[lang as keyof typeof categorySlugMappings];
      const pathSegment = categoryPathSegments[lang as keyof typeof categoryPathSegments] || 'category';
      const localizedSlug = mapping?.[englishCategoryId] || englishCategoryId;

      return {
        lang,
        url: `${base}${prefix}/blog/${pathSegment}/${localizedSlug}`,
      };
    }

    // Special handling for knowledge article URLs
    // Pattern: /knowledge/english-slug → /de/wissen/german-slug
    if (normalizedPath.startsWith('/knowledge/')) {
      const englishSlug = normalizedPath.replace('/knowledge/', '');

      const knowledgeBasePaths = {
        en: '/knowledge/',
        de: '/wissen/',
        'pt-BR': '/conhecimento/',
        'ms-MY': '/pengetahuan/',
        'es': '/conocimiento/',
        'fr': '/base-connaissances/',
        'ja': '/knowledge/',
        'id': '/pengetahuan/',
      };

      const basePath = knowledgeBasePaths[lang as keyof typeof knowledgeBasePaths] || '/knowledge/';

      // Get the localized slug for this language (sync version)
      const localizedSlug = getLocalizedKnowledgeSlugSync(englishSlug, lang);

      return {
        lang,
        url: `${base}${prefix}${basePath}${localizedSlug}`,
      };
    }

    // Check if this path has custom translations (for regular pages)
    const mappings = urlMappings[normalizedPath];
    if (mappings && mappings[lang]) {
      // Use the translated slug for this language
      const localizedPath = mappings[lang];

      // Build the full URL with locale prefix
      // English doesn't need prefix, others do
      if (lang === 'en') {
        return {
          lang,
          url: `${base}${localizedPath}`,
        };
      } else {
        return {
          lang,
          url: `${base}${prefix}${localizedPath}`,
        };
      }
    }

    // For pages without custom translations, use the default path
    return {
      lang,
      url: `${base}${prefix}${normalizedPath}`,
    };
  });
};
